import React from 'react';
import { Line } from 'react-chartjs-2';
import { CumulativeHourlySales } from '@models/statistics';
import moment from 'moment';
// 추가: 통일 상수 import
import { CHART_CATEGORY_LABELS, CHART_CATEGORY_COLORS } from 'src/pages/Dashboard';

interface CumulativeSalesChartProps {
  data: CumulativeHourlySales;
}

export const CumulativeSalesChart: React.FC<CumulativeSalesChartProps> = ({ data }) => {
  const hours = Array.from({ length: 24 }, (_, i) => `${i}시`);
  const baseDate = moment(data.baseDate);
  const lastWeekDate = baseDate.clone().subtract(7, 'days');

  const chartData = {
    labels: hours,
    datasets: [
      {
        label: `${CHART_CATEGORY_LABELS.sum} (${baseDate.format('MM/DD')})`,
        data: data.today.map((item) => item.total),
        borderColor: CHART_CATEGORY_COLORS.sum,
        backgroundColor: 'rgba(66,66,66,0.1)',
        tension: 0.1,
        borderWidth: 2,
        pointBackgroundColor: CHART_CATEGORY_COLORS.sum,
      },
      {
        label: `${CHART_CATEGORY_LABELS.sum} (${lastWeekDate.format('MM/DD')})`,
        data: data.lastWeek.map((item) => item.total),
        borderColor: CHART_CATEGORY_COLORS.sum,
        backgroundColor: 'rgba(66,66,66,0.1)',
        borderDash: [5, 5],
        tension: 0.1,
        borderWidth: 2,
        pointBackgroundColor: CHART_CATEGORY_COLORS.sum,
      },
      {
        label: `${CHART_CATEGORY_LABELS.class} (${baseDate.format('MM/DD')})`,
        data: data.today.map((item) => item.class),
        borderColor: CHART_CATEGORY_COLORS.class,
        backgroundColor: 'rgba(255,61,143,0.1)', // #FF3D8F
        tension: 0.1,
        borderWidth: 2,
        pointBackgroundColor: CHART_CATEGORY_COLORS.class,
      },
      {
        label: `${CHART_CATEGORY_LABELS.class} (${lastWeekDate.format('MM/DD')})`,
        data: data.lastWeek.map((item) => item.class),
        borderColor: CHART_CATEGORY_COLORS.class,
        backgroundColor: 'rgba(255,61,143,0.1)', // #FF3D8F
        borderDash: [5, 5],
        tension: 0.1,
        borderWidth: 2,
        pointBackgroundColor: CHART_CATEGORY_COLORS.class,
      },
      {
        label: `${CHART_CATEGORY_LABELS.commerce} (${baseDate.format('MM/DD')})`,
        data: data.today.map((item) => item.commerce),
        borderColor: CHART_CATEGORY_COLORS.commerce,
        backgroundColor: 'rgba(121,80,239,0.1)', // #7950EF
        tension: 0.1,
        borderWidth: 2,
        pointBackgroundColor: CHART_CATEGORY_COLORS.commerce,
      },
      {
        label: `${CHART_CATEGORY_LABELS.commerce} (${lastWeekDate.format('MM/DD')})`,
        data: data.lastWeek.map((item) => item.commerce),
        borderColor: CHART_CATEGORY_COLORS.commerce,
        backgroundColor: 'rgba(121,80,239,0.1)', // #7950EF
        borderDash: [5, 5],
        tension: 0.1,
        borderWidth: 2,
        pointBackgroundColor: CHART_CATEGORY_COLORS.commerce,
      },
      {
        label: `${CHART_CATEGORY_LABELS.naver} (${baseDate.format('MM/DD')})`,
        data: data.today.map((item) => item.naver),
        borderColor: CHART_CATEGORY_COLORS.naver,
        backgroundColor: 'rgba(25,206,96,0.1)',
        tension: 0.1,
        borderWidth: 2,
        pointBackgroundColor: CHART_CATEGORY_COLORS.naver,
      },
      {
        label: `${CHART_CATEGORY_LABELS.naver} (${lastWeekDate.format('MM/DD')})`,
        data: data.lastWeek.map((item) => item.naver),
        borderColor: CHART_CATEGORY_COLORS.naver,
        backgroundColor: 'rgba(25,206,96,0.1)',
        borderDash: [5, 5],
        tension: 0.1,
        borderWidth: 2,
        pointBackgroundColor: CHART_CATEGORY_COLORS.naver,
      },
      {
        label: `${CHART_CATEGORY_LABELS.kakao} (${baseDate.format('MM/DD')})`,
        data: data.today.map((item) => item.kakao),
        borderColor: CHART_CATEGORY_COLORS.kakao,
        backgroundColor: 'rgba(254,229,0,0.1)',
        tension: 0.1,
        borderWidth: 2,
        pointBackgroundColor: CHART_CATEGORY_COLORS.kakao,
      },
      {
        label: `${CHART_CATEGORY_LABELS.kakao} (${lastWeekDate.format('MM/DD')})`,
        data: data.lastWeek.map((item) => item.kakao),
        borderColor: CHART_CATEGORY_COLORS.kakao,
        backgroundColor: 'rgba(254,229,0,0.1)',
        borderDash: [5, 5],
        tension: 0.1,
        borderWidth: 2,
        pointBackgroundColor: CHART_CATEGORY_COLORS.kakao,
      },
      {
        label: `${CHART_CATEGORY_LABELS.ezwel} (${baseDate.format('MM/DD')})`,
        data: data.today.map((item) => item.ezwel || 0),
        borderColor: CHART_CATEGORY_COLORS.ezwel,
        backgroundColor: 'rgba(13,110,253,0.1)',
        tension: 0.1,
        borderWidth: 2,
        pointBackgroundColor: CHART_CATEGORY_COLORS.ezwel,
      },
      {
        label: `${CHART_CATEGORY_LABELS.ezwel} (${lastWeekDate.format('MM/DD')})`,
        data: data.lastWeek.map((item) => item.ezwel || 0),
        borderColor: CHART_CATEGORY_COLORS.ezwel,
        backgroundColor: 'rgba(13,110,253,0.1)',
        borderDash: [5, 5],
        tension: 0.1,
        borderWidth: 2,
        pointBackgroundColor: CHART_CATEGORY_COLORS.ezwel,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('ko-KR').format(context.parsed.y) + '원';
            }
            return label;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value: string | number) {
            return new Intl.NumberFormat('ko-KR').format(Number(value)) + '원';
          },
        },
      },
    },
  };

  return (
    <div style={{ height: '400px', width: '100%' }}>
      <Line data={chartData} options={options} />
    </div>
  );
};
